import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  Skeleton,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useNavigate, useSearchParams } from "react-router-dom";
import BreadcrumbsNav from "../../components/common/BreadCrumbNav";
import { search_properties, SEARCH_TYPE_MAP } from "../../api/ControllApi";
import { useSettings } from "../../context/Settings";
import { assetUrl, ASSET_BASE } from "../../utils/baseUrls";

const CATEGORIES = [
  { label: "All", value: "all" },
  { label: "Land", value: "Land" },
  { label: "Gioo Nano Plots", value: "Gioo Nano plots" },
  { label: "Gio Rental Yield - Syndicate Plots", value: "Gio Rental Yield - Syndicate plots" },
  { label: "GIO Rental Yield", value: "Gio Rental Yield" },
  { label: "Flats / Villas", value: "flats/villas" },
];

// Derive detail route based on source array
const getDetailRoute = (item, source) => {
  if (source === "gio") return `/gioo-plots/details/${item.id}`;
  if (source === "syndicate") return `/syndicate-plots/details/${item.id}`;
  if (source === "rental") return `/rental-yield-plots/details/${item.id}`;
  if (source === "plot") return `/flats-villas/details/${item.id}`;
  return `/plot-marketplace/details/${item.id}`;
};

// Source badge colours
const SOURCE_COLORS = {
  gio: { bg: "#E8F5E9", color: "#2E7D32" },
  market: { bg: "#E3F2FD", color: "#1565C0" },
  syndicate: { bg: "#FFF3E0", color: "#E65100" },
  rental: { bg: "#F3E5F5", color: "#7B1FA2" },
  plot: { bg: "#FFFDE7", color: "#FBC02D" },
};

const SOURCE_LABELS = {
  gio: "GIOO Nano Plot",
  market: "Land / Plot",
  syndicate: "Syndicate Plot",
  rental: "Rental Yield",
  plot: "Flats / Villas",
};

const IMG_BASE = ASSET_BASE;

const isVideo = (url) => {
  if (typeof url !== 'string') return false;
  return url.match(/\.(mp4|webm|mkv|mov|avi)$/i);
};

const resolveImage = (img, fallbackLogo) => {
  if (!img) return fallbackLogo || "/assets/images/logo.png";

  let images = [];
  if (Array.isArray(img)) {
    images = img;
  } else if (typeof img === "string") {
    images = img.split(",").map(i => i.trim()).filter(Boolean);
  }

  if (images.length > 0) {
    // Find first non-video image
    const firstImage = images.find(file => !isVideo(file));
    if (firstImage) {
      if (firstImage.startsWith("http")) return firstImage;
      return IMG_BASE + firstImage;
    }

    // If all are videos, return the first one
    const firstVideo = images[0];
    if (firstVideo.startsWith("http")) return firstVideo;
    return IMG_BASE + firstVideo;
  }

  return fallbackLogo || "/assets/images/logo.png";
};

function ResultCard({ item, source, fallbackLogo }) {
  const navigate = useNavigate();
  const src = SOURCE_COLORS[source] || SOURCE_COLORS.market;
  const image = item.image || item.thumbnail;
  const displayName = item.property_name || item.name;
  const displayAddress = item.location || item.address;
  const displayArea = item.area_sqft || item.area;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid #f0f0f0",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.22s ease, box-shadow 0.22s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.10)",
        },
      }}
    >
      <Box sx={{ position: "relative", width: "100%", height: 190, overflow: "hidden" }}>
        <CardMedia
          component="img"
          image={resolveImage(image, fallbackLogo)}
          alt={displayName}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={(e) => { e.target.src = fallbackLogo || "/assets/images/logo.png"; }}
        />
        {/* Source badge */}
        <Chip
          label={SOURCE_LABELS[source]}
          size="small"
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            bgcolor: src.bg,
            color: src.color,
            fontWeight: 700,
            fontSize: 11,
            height: 22,
          }}
        />
        {/* Property type badge */}
        {item.property_type?.category_name && (
          <Chip
            label={item.property_type.category_name}
            size="small"
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              bgcolor: "rgba(255,255,255,0.92)",
              color: "#555",
              fontWeight: 600,
              fontSize: 11,
              height: 22,
            }}
          />
        )}
      </Box>

      <CardContent sx={{ flex: 1, pb: 0 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            mb: 0.5,
          }}
        >
          {displayName}
        </Typography>

        {displayAddress && (
          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <LocationOnIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
              }}
            >
              {displayAddress}
            </Typography>
          </Box>
        )}

        {(item.total_price || item.price) && (
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ color: "#2E7D32", mt: 0.5 }}
          >
            ₹{Number(item.total_price || item.price || item.rent_amount).toLocaleString("en-IN")}
            <Typography component="span" variant="caption" color="text.secondary" fontWeight={400}>
              {item.total_price ? " Total Price" : " "}
            </Typography>
          </Typography>
        )}

        {source === "gio" && item.price && (
          <Typography variant="caption" color="text.secondary" display="block">
            ₹{item.price} /sq.ft
          </Typography>
        )}

        {displayArea && (
          <Typography variant="caption" color="text.secondary">
            {displayArea} sq.ft
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ pt: 1, pb: 2, px: 2 }}>
        <Button
          variant="contained"
          size="small"
          fullWidth
          onClick={() => navigate(getDetailRoute(item, source))}
          sx={{
            borderRadius: "9999px",
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: "#9EB86D",
            color: "#fff",
            "&:hover": { backgroundColor: "#7ea050" },
          }}
        >
          View Details
        </Button>
      </CardActions>
    </Card>
  );
}

function SkeletonCard() {
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #f0f0f0" }}>
      <Skeleton variant="rectangular" height={190} animation="wave" />
      <CardContent>
        <Skeleton width="70%" height={24} animation="wave" />
        <Skeleton width="50%" height={18} animation="wave" sx={{ mt: 1 }} />
        <Skeleton width="40%" height={18} animation="wave" sx={{ mt: 0.5 }} />
      </CardContent>
      <Box px={2} pb={2}>
        <Skeleton variant="rectangular" height={34} animation="wave" sx={{ borderRadius: 5 }} />
      </Box>
    </Card>
  );
}
export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || searchParams.get("search") || searchParams.get("region") || "";
  const [inputValue, setInputValue] = useState(initialQuery);
  const [results, setResults] = useState({ geo: [], market: [], syndicate: [], rental: [], plot: [], all: [] });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const { settings } = useSettings();

  const fallbackLogo = settings?.data?.logo
    ? assetUrl(settings.data.logo)
    : "/assets/images/logo.png";

  const query = searchParams.get("q") || searchParams.get("search") || searchParams.get("region") || "";
  const type = searchParams.get("property_type") || "all";
  const page = parseInt(searchParams.get("page") || "1", 10);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 6,
    total: 0,
  });

  // Sync input when URL changes (e.g. back/forward navigation)
  useEffect(() => {
    setInputValue(query);
  }, [query]);

  // Debounce input → update URL params (which triggers fetch below)
  useEffect(() => {
    const trimmed = inputValue.trim();

    // If cleared, immediately update URL (fetch will show all data by type)
    if (!trimmed) {
      setSearchParams({ q: "", property_type: type, page: "1" }, { replace: true });
      return;
    }

    const timer = setTimeout(() => {
      setSearchParams({ q: trimmed, property_type: type, page: "1" }, { replace: true });
    }, 400);

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch whenever URL query or type changes (runs even with empty query to show all data)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setSearched(false);
      try {
        const res = await search_properties(query, type, page);
        if (res.status) {
          let items = res.data?.items || [];
          if (items && typeof items === "object" && !Array.isArray(items)) {
            items = Object.values(items);
          }
          setResults({
            all: items,
            // Keep specific keys for backward compatibility if any parts of the app still expect them
            geo: res.data?.geo || (type === "Gioo Nano plots" ? items : []),
            market: res.data?.market || (type === "Land" ? items : []),
            syndicate: res.data?.syndicate || (type === "Gio Rental Yield - Syndicate plots" ? items : []),
            rental: res.data?.rental || (type === "Gio Rental Yield" ? items : []),
            plot: res.data?.plot || (type === "flats/villas" ? items : []),
          });
          if (res.data?.pagination) {
            setPagination(res.data.pagination);
          } else {
            setPagination({ current_page: 1, last_page: 1, total: 0 });
          }
        } else {
          setResults({ geo: [], market: [], syndicate: [], rental: [], plot: [], all: [] });
          setPagination({ current_page: 1, last_page: 1, total: 0 });
        }
      } catch (err) {
        setResults({ geo: [], market: [], syndicate: [], rental: [], plot: [], all: [] });
        setPagination({ current_page: 1, last_page: 1, total: 0 });
      } finally {
        setLoading(false);
        setSearched(true);
      }
    };
    fetchData();
  }, [query, type, page]);

  const handleCategoryChange = (_, newType) => {
    if (!newType) return;
    setSearchParams({ q: query, property_type: newType, page: "1" });
  };

  const handlePageChange = (_, newPage) => {
    setSearchParams({ q: query, property_type: type, page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Instant search (button / Enter) — bypasses debounce
  const handleSearch = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    setSearchParams({ q: trimmed, property_type: type, page: "1" });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  // Flatten results based on active tab
  const allItems = (Array.isArray(results.all) ? results.all : []).map((item) => {
    let source = "market";
    if (type === "all") {
      source = item.type || "market";
    } else {
      source = SEARCH_TYPE_MAP[type] || "market";
    }
    if (source === "geo") source = "gio";
    return { item, source };
  });

  const filteredItems = allItems;
  const totalCount = pagination.total;

  return (
    <Container sx={{ py: 5 }}>
      <Box sx={{ mb: 2 }}>
        <BreadcrumbsNav />
      </Box>

      {/* Search bar */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 3,
          alignItems: "center",
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder='Search your property'
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            sx: { borderRadius: "9999px", bgcolor: "#f5f5f5" },
          }}
          sx={{ "& .MuiOutlinedInput-notchedOutline": { border: "1px solid #e0e0e0" } }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          sx={{
            borderRadius: "9999px",
            px: 4,
            py: 1,
            textTransform: "none",
            whiteSpace: "nowrap",
            bgcolor: "#9EB86D",
            color: "#fff",
            "&:hover": { bgcolor: "#7ea050" },
          }}
        >
          Search
        </Button>
      </Box>

      <Box
        sx={{
          overflowX: "auto",
          pb: 1,
          mb: 3,
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <ToggleButtonGroup
          value={type}
          exclusive
          onChange={handleCategoryChange}
          sx={{
            bgcolor: "#f5f5f5",
            borderRadius: "9999px",
            p: 0.6,
            gap: 1.0, // Reduced gap
            minWidth: "max-content",
            flexWrap: "nowrap",
          }}
        >
          {CATEGORIES.map((cat) => (
            <ToggleButton
              key={cat.value}
              value={cat.value}
              sx={{
                borderRadius: "9999px !important",
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                px: 2.5,
                py: 0.8,
                border: "none !important",
                color: "#555",
                "&.Mui-selected": {
                  background: "linear-gradient(to right, #C7DD94, #92AF5D)",
                  color: "#fff",
                  "&:hover": { background: "linear-gradient(to right, #b8d080, #7ea050)" },
                },
              }}
            >
              {cat.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Results headline */}
      {!loading && searched && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          {totalCount > 0
            ? `Showing ${totalCount} result${totalCount !== 1 ? "s" : ""} for "${query}"`
            : null}
        </Typography>
      )}

      {/* Results grid */}
      <Grid container spacing={3}>
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
              <SkeletonCard />
            </Grid>
          ))
        ) : filteredItems.length > 0 ? (
          filteredItems.map(({ item, source }, idx) => (
            <Grid key={`${source}-${item.id}-${idx}`} size={{ xs: 12, sm: 6, md: 4 }}>
              <ResultCard item={item} source={source} fallbackLogo={fallbackLogo} />
            </Grid>
          ))
        ) : searched ? (
          <Grid size={12}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="50vh"
            >
              <Box
                component="img"
                src="/assets/images/nodatafound.png"
                alt="No results"
                sx={{ width: { xs: 260, sm: 340 }, mb: 2 }}
              />
              <Typography color="text.secondary" fontWeight={500}>
                No results found for "{query}"
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Try different keywords or switch the category tab.
              </Typography>
            </Box>
          </Grid>
        ) : !query ? (
          <Grid size={12}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="40vh"
            >
              <SearchIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
              <Typography color="text.secondary">
                Enter a keyword above and click Search.
              </Typography>
            </Box>
          </Grid>
        ) : null}
      </Grid>

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={pagination.last_page}
            page={page}
            onChange={handlePageChange}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                fontWeight: 600,
              },
              "& .Mui-selected": {
                bgcolor: "#9EB86D !important",
                color: "#fff",
              },
            }}
          />
        </Box>
      )}
    </Container>
  );
}
